import ContinuationNote from './ContinuationNote';
import { driverKey } from './corrections';
import { displayDiagnosis, metricLabel } from '../agent-common/diagnosisLabels';
import React, { useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DiagnoseTrace from '../LandingAgent/DiagnoseTrace';
import TraceLink from '../agent-common/TraceLink';
import WalkSteps from './WalkSteps';
import TierChip from './TierChip';
import EvidenceStrip from './EvidenceStrip';
import VerdictEditor from './VerdictEditor';
import ContributeBox from './ContributeBox';
import { describeValidation, targetOfEnvelope } from './contributions';
import HiddenNote from './HiddenNote';
import type { Annotation, DiagnoseDriver } from '../../apis';
import { stromaSectionLabel, stromaWalkCard } from '../LandingAgent/askPresentation';

const VERDICT_SEVERITY: Record<string, 'success' | 'info' | 'warning' | 'error'> = {
  explained: 'success',
  corrected: 'success',
  within_normal: 'info',
  preview: 'info',
  data_late: 'warning',
  immature: 'info',
  insufficient_data: 'warning',
  unexplained: 'warning',
  error: 'error',
};

/**
 * A verdict that has nothing to attribute — no driver story is told. Data
 * that is late established no movement, so an event that overlaps the
 * window is evidence in the steps, not a driver.
 */
const NO_DRIVERS = new Set(['within_normal', 'insufficient_data', 'data_late', 'immature', 'error', 'preview']);

const fmtPct = (v: number | null | undefined) =>
  v == null || Number.isNaN(v) ? '' : `${v >= 0 ? '+' : ''}${(v as number).toFixed(1)}%`;

/**
 * The top drivers, at most three, in the order the walk found them: the
 * causal path (each hop with its move), the dimension that concentrates the
 * change, and the event that lines up with it.
 */
export function topDrivers(envelope: any): string[] {
  const out: string[] = [];
  const target = envelope?.target?.metric;
  const byMetric = new Map<string, any>((envelope?.chain || []).map((c: any) => [c.metric, c]));
  for (const m of (envelope?.best_path || []) as string[]) {
    if (m === target) continue;
    const node = byMetric.get(m);
    out.push(node && node.delta_pct != null ? `${(node?.label || metricLabel(m))} ${fmtPct(node.delta_pct)}` : (node?.label || metricLabel(m)));
  }
  const seg = envelope?.segments;
  if (seg?.segments?.length) {
    out.push(`${seg.dimension}=${String(seg.segments[0].segment)} carries ${Math.round((seg.top_share || 0) * 100)}% of the change`);
  }
  const ev = envelope?.annotations?.[0];
  if (ev) out.push(`${ev.title} (${String(ev.ts).slice(0, 10)})`);
  return out.slice(0, 3);
}

/**
 * How much of the change a driver carries, as a share of the bar: a segment's
 * own share; a metric hop's move relative to the largest hop shown. An event
 * has no share — it lines up in time, it does not carry a fraction.
 */
export function driverShare(d: DiagnoseDriver, all: DiagnoseDriver[]): number | null {
  if (d.kind === 'segment') return typeof d.share === 'number' ? Math.max(0, Math.min(1, d.share)) : null;
  if (d.kind === 'metric' && typeof d.delta_pct === 'number') {
    const largest = Math.max(...all.filter((x) => x.kind === 'metric' && typeof x.delta_pct === 'number').map((x) => Math.abs(x.delta_pct as number)), 0);
    return largest > 0 ? Math.abs(d.delta_pct) / largest : null;
  }
  return null;
}

/** How a driver reads on the card: the hop with its move, the segment with its share, the event with its date. */
export function driverLabel(d: DiagnoseDriver): string {
  if (d.kind === 'metric') return `${d.label || d.id} ${fmtPct(d.delta_pct)}`.trim();
  if (d.kind === 'segment') return `${d.dimension}=${String(d.segment)} carries ${Math.round((d.share || 0) * 100)}% of the change`;
  return `${d.title || d.id} (${String(d.ts || '').slice(0, 10)})`;
}

interface Props {
  envelope: any;
  /** The traced session the walk wrote (#184): the Trace button and the full trace open the same id. */
  sessionId?: string | null;
  onKeepDigging?: () => void;
  digging?: boolean;
  /** The perspective this answer was asked in — a correction is scoped to it (#186). */
  perspective?: string | null;
  /** A correction was saved: the caller lays it onto the turn (client mirror of the server's layering). */
  onCorrected?: (saved: Annotation & Record<string, any>) => void;
  /** Raise the evidence bar for this answer (#187): re-run the walk at `bar`, in place. Not remembered. */
  onRaiseBar?: (bar: string) => void;
  /** A contribution was saved or its dry run landed (#193): the caller lays it onto the turn. */
  onContributed?: (saved: Annotation & Record<string, any>) => void;
  /** #1506 (flag on): the turn's action row owns Correct this; each bump opens the correction, and the card's own
   *  button is left out. Undefined (flag off) keeps the card's button. */
  correctRequest?: number;
  /** #1507 (flag on): the walk's steps and its full trace are in the answer's panel (Evidence, Data); the card keeps
   *  the verdict, every tier chip and withholding note, and the steps that carry tiers (connections) */
  simple?: boolean;
}

/** What the card and the panel's Evidence tab both read off an envelope, computed once so their counts never differ. */
export function whyParts(envelope: any) {
  const kind = String(envelope?.verdict?.kind || '');
  // Drivers with earned tiers (#185); an envelope traced before tiers falls back to the plain list.
  const tiered: DiagnoseDriver[] = NO_DRIVERS.has(kind) ? [] : ((envelope?.drivers as DiagnoseDriver[] | undefined) || []).slice(0, 5);
  const drivers = NO_DRIVERS.has(kind) || tiered.length > 0 ? [] : topDrivers(envelope);
  // Human knowledge is not an invented story: a note a person added on this
  // metric is shown even when the number is within normal and no driver story
  // is told. On a walked verdict it already rides in `tiered`; here we surface
  // it on the verdicts that suppress drivers, in its own labelled block.
  const notes: DiagnoseDriver[] = NO_DRIVERS.has(kind)
    ? ((envelope?.drivers as DiagnoseDriver[] | undefined) || []).filter((d) =>
      d.annotation_kind === 'contribution' || (d.kind === 'event' && d.routed && d.annotation_kind !== 'pipeline_run'))
    : [];
  const connectionsHidden = Number(envelope?.policy?.connections_hidden || 0);
  const driversHidden = Math.max(0, Number(envelope?.policy?.hidden_count || 0) - connectionsHidden);
  return { kind, tiered, drivers, notes, connectionsHidden, driversHidden };
}

/** The steps that carry tiers and qualifications of their own (#426): they stay beside the verdict with the flag on. */
const tieredSteps = (steps: any[] | null | undefined) => (steps || []).filter((s: any) => s?.id === 'connections' && s.detail);

/**
 * The panel's Evidence tab for a walk (#1507, flag on): every step with its checks, every driver and note with its
 * tier and ALL its sources (the card shows the first four), and the same withholding notes the card shows. It adds
 * detail; it is never the only place a tier or a withholding is shown.
 */
export function WhyEvidence({ envelope: storedEnvelope, onRaiseBar, digging }: { envelope: any; onRaiseBar?: (bar: string) => void; digging?: boolean }) {
  const envelope = storedEnvelope ? displayDiagnosis(storedEnvelope) : storedEnvelope;
  if (!envelope?.verdict) return null;
  const { tiered, drivers, notes, connectionsHidden, driversHidden } = whyParts(envelope);
  const items = [...notes, ...tiered];
  return (
    <Stack spacing={2} data-testid="why-evidence">
      <WalkSteps steps={envelope.steps} />
      {envelope.contributions_note && (
        <Typography variant="caption" sx={{ color: 'var(--ok)', fontWeight: 600 }}>✓ {envelope.contributions_note}</Typography>
      )}
      {items.map((d, i) => (
        <Box key={`${d.kind}:${d.id ?? i}`} data-testid="why-evidence-item" data-tier={d.tier}
          sx={{ border: '1px solid var(--line)', borderRadius: '10px', p: '12px 14px', display: 'grid', gap: 0.75 }}>
          <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
            <TierChip tier={d.tier} />
            <Typography sx={{ fontSize: 13.5, fontWeight: 500 }}>{driverLabel(d)}</Typography>
            {driverShare(d, tiered) != null && (
              <Typography variant="caption" sx={{ fontWeight: 700, ml: 'auto' }}>{Math.round(driverShare(d, tiered)! * 100)}%</Typography>
            )}
          </Box>
          {d.corrected_by && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              corrected by {d.corrected_by.name || d.corrected_by.id || 'someone'} · {String(d.corrected_by.at || '').slice(0, 10)}
            </Typography>
          )}
          {(d.added_by?.name || d.added_by?.id) && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {d.annotation_kind === 'contribution' ? `added by ${d.added_by?.name || d.added_by?.id} · ${describeValidation(d.validation)}` : `noted by ${d.added_by?.name || d.added_by?.id}`}
            </Typography>
          )}
          <EvidenceStrip sources={d.sources} max={Number.MAX_SAFE_INTEGER} />
        </Box>
      ))}
      {envelope.policy && driversHidden > 0 && (
        <HiddenNote policy={envelope.policy} count={driversHidden} what="drivers" onRaise={onRaiseBar} raising={digging} />
      )}
      {envelope.policy && connectionsHidden > 0 && (
        <HiddenNote policy={envelope.policy} count={connectionsHidden} what="connections" onRaise={onRaiseBar} raising={digging} />
      )}
      {drivers.length > 0 && (
        <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
          {drivers.map((d, i) => <Typography component="li" key={i} sx={{ fontSize: 13, lineHeight: 1.6 }}>{d}</Typography>)}
        </Box>
      )}
    </Stack>
  );
}

/**
 * The WHY card (#184): verdict, the four steps with their statuses, the top
 * drivers, and the full trace behind one toggle. An in-range number gets
 * the verdict and its z — no driver story is invented for it.
 */
export default function WhyCard({ envelope: storedEnvelope, sessionId, onKeepDigging, digging, perspective, onCorrected, onRaiseBar, onContributed, correctRequest, simple = false }: Props) {
  const envelope = storedEnvelope ? displayDiagnosis(storedEnvelope) : storedEnvelope;
  const [showTrace, setShowTrace] = useState(false);
  const [editing, setEditing] = useState(false);
  useEffect(() => { if (correctRequest) setEditing(true); }, [correctRequest]);
  const [contributing, setContributing] = useState<{ driver: DiagnoseDriver | null } | null>(null);
  const contributeTarget = onContributed && perspective ? targetOfEnvelope(storedEnvelope, perspective, sessionId) : null;
  const suggestedDimensions: string[] = Array.from(new Set([envelope?.segments?.dimension, ...((envelope?.drivers || []) as DiagnoseDriver[]).map((d) => d.dimension)].filter(Boolean))) as string[];
  if (!envelope?.verdict) return null;
  const verdict = envelope.verdict;
  const layers: any[] = envelope.verdict_layers || [];
  const corrected = layers.length > 1 ? layers[layers.length - 1] : null;
  const { kind, tiered, drivers, notes, connectionsHidden, driversHidden } = whyParts(envelope);
  const significance = (envelope.steps || []).find((s: any) => s.id === 'significance');
  const z = significance?.detail?.z;
  const canDig = !!onKeepDigging && envelope.budget?.effort !== 'deep' && !['within_normal', 'immature'].includes(kind);

  return (
    <Stack spacing={1.5} sx={{ mt: 1, ...stromaWalkCard }} data-testid="why-card">
      <Alert severity={VERDICT_SEVERITY[kind] ?? 'info'} icon={false} sx={{ borderRadius: '12px', '& .MuiAlert-message': { width: '100%' } }}>
        <Typography variant="overline" sx={{ letterSpacing: 1 }}>
          {kind.replace(/_/g, ' ')} · confidence {verdict.confidence}
        </Typography>
        <Typography sx={{ fontWeight: 600, mt: 0.5 }}>{verdict.headline}</Typography>
        {corrected && (
          <Typography variant="body2" sx={{ mt: 0.5 }} data-testid="corrected-by">
            corrected by {corrected.asserted_by_name || corrected.asserted_by || 'someone'} · {String(corrected.asserted_at || '').slice(0, 10)}
            {' — '}originally: <em>{layers[0]?.verdict?.headline}</em>
          </Typography>
        )}
        {kind === 'within_normal' && typeof z === 'number' && (
          <Typography variant="body2" sx={{ mt: 0.5 }} data-testid="within-normal-z">
            Within normal range (z={z.toFixed(1)}
            {typeof significance?.detail?.threshold === 'number' ? `, threshold ${significance.detail.threshold.toFixed(1)}` : ''})
            — no statistically significant movement against this baseline{notes.length > 0 ? `, but ${notes.length} note${notes.length === 1 ? '' : 's'} noted here` : ''}.
          </Typography>
        )}
        {kind === 'data_late' && verdict.late_run?.dag_id && (
          <Typography variant="body2" sx={{ mt: 0.5 }} data-testid="why-late-run">
            Pipeline: {verdict.late_run.dag_id}
            {verdict.late_run.landed
              ? ` landed ${String(verdict.late_run.ended_at || '').replace('T', ' ').slice(0, 16)}${verdict.late_run.lateness ? ` (${verdict.late_run.lateness} late)` : ''}`
              : ` has not landed${verdict.late_run.lateness ? ` (${verdict.late_run.lateness} past its run)` : ''}`}
            {verdict.late_run.built_before && verdict.late_run.built_before.length > 0
              ? ` — ${verdict.late_run.built_before.map((b: { dag_id?: string }) => b.dag_id).filter(Boolean).join(', ')} ran before it` : ''}
            {verdict.late_run.reads ? ` · this number reads ${verdict.late_run.reads}` : ''}
          </Typography>
        )}
        {verdict.caveat && (
          <Typography variant="body2" sx={{ mt: 0.5, fontStyle: 'italic' }}>⚠ {verdict.caveat}</Typography>
        )}
      </Alert>
      <ContinuationNote continuation={envelope.continuation} />

      <WalkSteps steps={simple ? tieredSteps(envelope.steps) : envelope.steps} />
      {envelope.contributions_note && (
        <Typography variant="caption" sx={{ color: 'var(--ok)', fontWeight: 600 }} data-testid="why-contributions-note">
          ✓ {envelope.contributions_note}
        </Typography>
      )}

      {notes.length > 0 && (
        <Box data-testid="why-notes">
          <Typography variant="caption" sx={stromaSectionLabel}>
            Noted here
          </Typography>
          <Box component="ul" sx={{ m: 0, mt: 0.25, pl: 2.5, display: 'grid', gap: 0.75, listStyle: 'none' }}>
            {notes.map((d, i) => (
              <Box component="li" key={`note:${d.id ?? i}`} data-testid="why-note" data-tier={d.tier}>
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: 13, lineHeight: 1.6 }}>{driverLabel(d)}</Typography>
                  <TierChip tier={d.tier} />
                  {d.annotation_kind === 'contribution' && (
                    <Typography variant="caption" sx={{ color: d.validation?.outcome === 'passed' && !d.validation?.historical ? 'var(--ok)' : 'text.secondary' }} data-testid="note-added-by">
                      added by {d.added_by?.name || d.added_by?.id || 'someone'} · {describeValidation(d.validation)}
                    </Typography>
                  )}
                  {d.annotation_kind !== 'contribution' && (d.added_by?.name || d.added_by?.id) && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }} data-testid="note-noted-by">
                      noted by {d.added_by?.name || d.added_by?.id}
                    </Typography>
                  )}
                </Box>
                <EvidenceStrip sources={d.sources} />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {tiered.length > 0 && (
        <Box data-testid="why-drivers">
          <Typography variant="caption" sx={stromaSectionLabel}>
            Drivers
          </Typography>
          <Box component="ol" sx={{ m: 0, mt: 0.25, pl: 2.5, display: 'grid', gap: 0.75 }}>
            {tiered.map((d, i) => (
              <Box component="li" key={`${d.kind}:${d.id ?? i}`} data-testid="why-driver" data-kind={d.kind} data-tier={d.tier}>
                <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: 13, lineHeight: 1.6 }}>{driverLabel(d)}</Typography>
                  <TierChip tier={d.tier} />
                  {driverShare(d, tiered) != null && (
                    <Typography variant="caption" sx={{ fontWeight: 700, ml: 'auto' }} data-testid="why-driver-share">{Math.round(driverShare(d, tiered)! * 100)}%</Typography>
                  )}
                  {d.corrected_by && (
                    <Typography variant="caption" sx={{ color: 'text.secondary' }} data-testid="driver-corrected-by">
                      corrected by {d.corrected_by.name || d.corrected_by.id || 'someone'} · {String(d.corrected_by.at || '').slice(0, 10)}
                    </Typography>
                  )}
                  {d.annotation_kind === 'contribution' && (
                    <Typography variant="caption" sx={{ color: d.validation?.outcome === 'passed' && !d.validation?.historical ? 'var(--ok)' : 'text.secondary' }} data-testid="driver-added-by">
                      added by {d.added_by?.name || d.added_by?.id || 'someone'} · {describeValidation(d.validation)}
                    </Typography>
                  )}
                  {contributeTarget && !contributing && d.annotation_kind !== 'contribution' && (
                    <Button size="small" variant="text" onClick={() => setContributing({ driver: storedEnvelope.drivers?.find((driver: DiagnoseDriver) => driverKey(driver) === driverKey(d)) || null })} sx={{ py: 0, minHeight: 0, fontSize: '0.7rem' }} data-testid="driver-contribute">
                      Add what you know
                    </Button>
                  )}
                </Box>
                {driverShare(d, tiered) != null && (
                  <Box sx={{ height: 6, borderRadius: 3, bgcolor: 'var(--line-soft)', my: 0.5, overflow: 'hidden' }} data-testid="why-driver-bar">
                    <Box sx={{ height: '100%', width: `${Math.round(driverShare(d, tiered)! * 100)}%`, bgcolor: d.tier === 'confirmed' ? 'var(--accent)' : d.tier === 'emerging' ? 'var(--warn)' : 'var(--ink-3)' }} />
                  </Box>
                )}
                <EvidenceStrip sources={d.sources} />
              </Box>
            ))}
          </Box>
        </Box>
      )}
      {/* `hidden_count` is the TOTAL — drivers and connections together (#426). Labelling all of it as
          drivers and then adding the connections again counts them twice, so the drivers are what is left
          after subtracting them. A v1 envelope carries no connections count and reads exactly as before. */}
      {envelope.policy && driversHidden > 0 && (
        <HiddenNote policy={envelope.policy} count={driversHidden} what="drivers" onRaise={onRaiseBar} raising={digging} />
      )}
      {envelope.policy && connectionsHidden > 0 && (
        <HiddenNote policy={envelope.policy} count={connectionsHidden} what="connections"
          onRaise={onRaiseBar} raising={digging} />
      )}
      {drivers.length > 0 && (
        <Box data-testid="why-drivers">
          <Typography variant="caption" sx={{ color: 'text.secondary', letterSpacing: 0.5, textTransform: 'uppercase' }}>
            Top drivers
          </Typography>
          <Box component="ol" sx={{ m: 0, mt: 0.25, pl: 2.5 }}>
            {drivers.map((d, i) => (
              <Typography component="li" key={i} sx={{ fontSize: 13, lineHeight: 1.6 }}>{d}</Typography>
            ))}
          </Box>
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
        {!simple && <Box
          role="button" tabIndex={0} aria-expanded={showTrace}
          onClick={() => setShowTrace(!showTrace)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setShowTrace(!showTrace); } }}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', color: 'text.secondary',
                '&:focus-visible': { outline: 2, outlineColor: 'primary.main' } }}
        >
          <ExpandMoreIcon fontSize="small" sx={{ transform: showTrace ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform .15s' }} />
          <Typography variant="caption">{showTrace ? 'Hide full trace' : 'Open full trace'}</Typography>
        </Box>}
        {canDig && (
          <Button size="small" variant="outlined" disabled={!!digging} onClick={onKeepDigging}>
            {digging ? 'Digging…' : 'Keep digging'}
          </Button>
        )}
        {correctRequest === undefined && onCorrected && !editing && envelope.target?.metric && (
          <Button size="small" variant="text" onClick={() => setEditing(true)}>This is wrong — correct it</Button>
        )}
        {contributeTarget && !contributing && (
          <Button size="small" variant="text" onClick={() => setContributing({ driver: null })} data-testid="why-contribute">Add what you know</Button>
        )}
      </Box>
      {contributing && contributeTarget && onContributed && (
        <ContributeBox
          target={contributeTarget}
          driver={contributing.driver}
          displayDriverLabel={envelope.drivers?.find((d: DiagnoseDriver) => contributing.driver && driverKey(d) === driverKey(contributing.driver))?.label}
          dimensions={suggestedDimensions}
          onSaved={onContributed}
          onValidated={onContributed}
          onCancel={() => setContributing(null)}
        />
      )}

      {editing && onCorrected && (
        <VerdictEditor
          envelope={storedEnvelope}
          perspective={perspective}
          sessionId={sessionId}
          onCancel={() => setEditing(false)}
          onSaved={(saved) => { setEditing(false); onCorrected(saved); }}
        />
      )}

      {!simple && showTrace && (
        <Box data-testid="why-full-trace" sx={{ mt: 0.5, p: 1.5, border: '1px solid var(--line-soft)', borderRadius: '10px', bgcolor: 'var(--sunk)' }}>
          {sessionId && (
            <Box sx={{ mb: 0.5 }}><TraceLink sessionId={sessionId} /></Box>
          )}
          <DiagnoseTrace envelope={storedEnvelope} />
        </Box>
      )}
    </Stack>
  );
}
